/**
 * Compact rendering of an AuditLog before/after pair for the table's "Зміна" column.
 * Flat objects (no nested objects/arrays as values) render as "field: old → new, field2: ...";
 * anything else (nested, arrays, non-objects, mismatched shapes) falls back to JSON.
 */

type PlainRecord = Record<string, unknown>;

function isFlatObject(value: unknown): value is PlainRecord {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  return Object.values(value as PlainRecord).every((v) => v === null || typeof v !== 'object');
}

function display(value: unknown): string {
  if (value === undefined) return '—';
  if (value === null) return 'null';
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
}

export function formatAuditChange(before: unknown, after: unknown): string {
  if (before == null && after == null) return '—';

  const bothFlat = (before === undefined || isFlatObject(before)) && (after === undefined || isFlatObject(after));
  if (bothFlat) {
    const b = (before as PlainRecord | undefined) ?? {};
    const a = (after as PlainRecord | undefined) ?? {};
    const keys = [...new Set([...Object.keys(b), ...Object.keys(a)])];
    const changed = keys.filter((k) => JSON.stringify(b[k]) !== JSON.stringify(a[k]));
    if (changed.length === 0) return '—';
    return changed.map((k) => `${k}: ${display(b[k])} → ${display(a[k])}`).join(', ');
  }

  const parts: string[] = [];
  if (before !== undefined && before !== null) parts.push(`before: ${JSON.stringify(before)}`);
  if (after !== undefined && after !== null) parts.push(`after: ${JSON.stringify(after)}`);
  return parts.join(' ') || '—';
}
